import React, { useCallback, useState } from 'react';
import Grid from '../../../../../base-ui/Grid';
import { AddPermitModal } from '../modalContent/AddPermitModal';
import { DeletePermitModal } from '../modalContent/DeletePermitModal';
import { EditPermitModal } from '../modalContent/EditPermitModal';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ConfigContext } from '../../../../../base-ui/lib';
import { GridActionsCellItem } from '@mui/x-data-grid';
import PermitGridToolbar from './PermitGridToolbar';
import EmptySearchState from '../../../../../base-ui/Grid/components/EmptySearchState';
import { useCompanyState } from '../../../../../stateProviders/CompanyStateProvider';

const EmptyStateComponent = () => <EmptySearchState />;

// Cast JS Grid module to any for TS props compatibility
const AnyGrid = Grid as any;

export const PermitGrid = () => {
	const { config }: any = React.useContext(ConfigContext);
	const companyState = useCompanyState();
	const gridRef = React.useRef<any>(null);

	const logisticCostsId = companyState.logisticCosts?._id;
	const [addModalOpen, setAddModalOpen] = React.useState(false);
	const [editModalOpen, setEditModalOpen] = React.useState(false);
	const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
	const [selectedRow, setSelectedRow] = React.useState<any>(null);

	const [filter, setFilter] = useState('');
	const [allData, setAllData] = useState([]);

	const renderToolbar = useCallback(
		() => (
			<PermitGridToolbar
				filter={filter}
				setFilter={setFilter}
				handleRefresh={() => gridRef.current?.onRefresh()}
			/>
		),
		[filter, setFilter]
	);

	const filterList = [
		{
			id: 'permitName',
			label: 'Permit Name',
			dataType: 'string',
			defaultValue: '',
		},
		{
			id: 'permitDescription',
			label: 'Description',
			dataType: 'string',
			defaultValue: '',
		},
		{
			id: 'permitAmount',
			label: 'Cost ($)',
			dataType: 'number',
			defaultValue: '',
		},
	];

	const headCells = [
		{
			field: 'permitName',
			headerName: 'Permit Name',
			sortable: true,
			flex: 2, // Most important, more space
		},
		{
			field: 'permitDescription',
			headerName: 'Description',
			sortable: true,
			flex: 3, // Description can be long, most space
		},
		{
			field: 'permitAmount',
			headerName: 'Cost ($)',
			type: 'number',
			valueFormatter: (value) => {
				const numValue = Number(value);
				if (isNaN(numValue) || value == null) return '$0.00';
				return numValue.toLocaleString('en-US', {
					style: 'currency',
					currency: 'USD',
				});
			},
			flex: 1, // Least important, less space
		},
		{
			field: 'actions',
			type: 'actions',
			getActions: ({ row }) => [
				<GridActionsCellItem
					key="actions"
					label="Edit"
					onClick={() => {
						setEditModalOpen(true);
						setSelectedRow(row);
					}}
					showInMenu
				/>,
				<GridActionsCellItem
					key="delete"
					label="Delete"
					onClick={() => {
						setDeleteModalOpen(true);
						setSelectedRow(row);
					}}
					showInMenu
				/>,
			],
			flex: 0.5, // Smallest, just for actions
		},
	];

	return (
		<Box
			display="flex"
			flexDirection="column"
			maxWidth={{ xs: '100%', sm: 600, md: 800, lg: 1000 }}
			height="100%"
		>
			{/* Sticky Header */}
			<Box
				sx={{
					position: 'sticky',
					top: 0,
					backgroundColor: 'background.default',
					zIndex: 10,
					pb: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
					mb: 2,
				}}
			>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					mb={2}
				>
					<Stack spacing={1}>
						<Typography variant="h2" fontWeight={700}>
							Permits
						</Typography>
						<Typography variant="subtitle1" color="text.secondary">
							Create permits that can be applied to quotes
						</Typography>
					</Stack>
					<Stack direction="row" spacing={2} alignItems="flex-end">
						{renderToolbar()}
						<Button
							variant="contained"
							color="success"
							onClick={() => setAddModalOpen(true)}
						>
							Add New Permit
						</Button>
					</Stack>
				</Stack>
			</Box>

			<Box sx={{ flex: 1, overflow: 'hidden' }}>
				<AnyGrid
					ref={gridRef}
					headCells={headCells}
					filterList={filterList}
					searchEndpont={`${config.uri.base}/logisticCosts/permits`}
					searchAdditionalQueryParams={
						logisticCostsId ? { logisticCostsId } : {}
					}
					pageSizeOptions={[10, 25, 50]}
					selectableRows={false}
					hybridMode={true}
					initialPageSize={1000}
					onDataLoad={(data) => setAllData(data)}
					options={{ filter }}
					customNoRowsOverlay={EmptyStateComponent}
				/>
			</Box>
			{addModalOpen && (
				<AddPermitModal
					gridRef={gridRef}
					modalOpen={addModalOpen}
					setModalOpen={setAddModalOpen}
				/>
			)}
			{editModalOpen && selectedRow && (
				<EditPermitModal
					rowData={selectedRow}
					gridRef={gridRef}
					modalOpen={editModalOpen}
					setModalOpen={setEditModalOpen}
				/>
			)}
			{deleteModalOpen && selectedRow && (
				<DeletePermitModal
					rowData={selectedRow}
					gridRef={gridRef}
					modalOpen={deleteModalOpen}
					setModalOpen={setDeleteModalOpen}
				/>
			)}
		</Box>
	);
};
